import { Component, DestroyRef, inject, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { AutomationTab } from '../tabs/enums/tabs.enum';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { auditTime, catchError, EMPTY, filter, finalize, forkJoin, map, of, Subject, switchMap, takeUntil } from 'rxjs';
import { normalizeBoardFilterData } from './board-filters.utils';
import { BoardService } from '../../services/board.service';
import {
  BoardCard,
  BoardColumn,
  BoardField,
  BoardManager,
  BoardFunnel,
  BoardColumnColor,
  DynamicBoardCard,
  MoveCardPayload,
  CardViewer,
  BoardCardChat,
  BoardCardMoveRequest,
  BoardFieldsUpdatedEvent,
  ImportProgressEvent,
  ExportProgressEvent,
  FunnelDeletedEvent,
  LeadConversionResultEvent,
  LeadConversionRequest,
  BoardMassActionResponse,
  BoardFilterCondition,
  BoardFilterMode,
  BoardFilterSetData,
} from './board-v3.types';
import { FUNNEL_STATUS, FUNNEL_TYPE } from '../../enum/constants';
import { BoardV3RealtimeService } from './board-v3-realtime.service';
import { boardV3DragActionDropZoneIds } from './drag-action-footer/drag-action-footer.utils';
import { StackedSidebarService } from '../../services/stacked-sidebar.service';
import { BoardV3FiltersSidebarResult, SidebarType } from '../layout/stacked-sidebar/stacked-sidebar.interface';
import { BaseService } from '../../services/base.service';
import { ModalService } from '../../services/modal.service';
import { DialogService } from '../../services/dialog.service';
import { BoardColumnEditComponent } from '../../modals/board-column-edit/board-column-edit.component';
import { ChatService } from '../../services/chat.service';
import { NotificationService } from '../../services/notifications.service';
import { TranslateService } from '@ngx-translate/core';
import { BoardV3PresenceService } from './board-v3-presence.service';
import type { LeadConversionResult } from './sidebars/lead-conversion/lead-conversion.component';
import { WarehouseService } from '../../services/warehouse.service';
import { getApiError } from '../warehouse/warehouse.utils';

const SELECTED_FUNNEL_STORAGE_KEY = 'board_v3_selected_funnel_id_';

type ChatUnreadState = {
  id?: string;
  unread_count?: number | null;
  is_mark_unread?: boolean | null;
};

interface MoveCardOptions {
  card: DynamicBoardCard;
  fromColumnId: string;
  toColumnId: string;
  toIndex: number;
  fromIndex?: number;
  leadConversion?: LeadConversionRequest;
}

interface PendingMoveNotification {
  toColumnId: string;
  isLeadConversion: boolean;
  dealFunnelId?: string;
}

interface PendingBulkLeadConversion {
  dealFunnelId: string;
  expectedCount: number;
  processedCount: number;
  movedCount: number;
  failedCount: number;
  revertedCount: number;
}

@Component({
  selector: 'app-board-v3',
  templateUrl: './board.component.html',
  styleUrls: ['./board.component.scss'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  host: { class: 'flex h-full min-h-0 flex-col overflow-hidden' },
})
export class BoardV3Component implements OnInit, OnDestroy {
  @Input() type: FUNNEL_TYPE = FUNNEL_TYPE.LEAD;
  protected get title(): string {
    return this.type === FUNNEL_TYPE.LEAD ? 'leads' : 'deals';
  }

  private readonly boardService = inject(BoardService);
  private readonly realtimeService = inject(BoardV3RealtimeService);
  private readonly stackedSidebarService = inject(StackedSidebarService);
  private readonly baseService = inject(BaseService);
  private readonly modalService = inject(ModalService);
  private readonly dialogService = inject(DialogService);
  private readonly chatService = inject(ChatService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly presenceService = inject(BoardV3PresenceService);
  private readonly warehouseService = inject(WarehouseService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly funnelRealtimeDestroy$ = new Subject<void>();
  private readonly locallyRemovedCardIds = new Set<string>();
  private readonly movingCardIds = new Set<string>();
  private readonly pendingMoveNotifications = new Map<string, PendingMoveNotification>();
  private readonly pendingBulkLeadConversions = new Map<string, PendingBulkLeadConversion>();

  funnelId: string | null = null;
  private isSearchingDuplicates = false;
  private isResettingPositions = false;
  funnelName: string = '';
  funnelViewFields: string[] = [];
  funnelCreateFields: string[] = [];
  filterChipName: string = '';
  activeFilterData: BoardFilterSetData = normalizeBoardFilterData();
  activeFilterName: string = '';
  activeFilterId: string | null = null;
  private readonly boardRequestsCancelled$ = new Subject<void>();
  private readonly filteredRefresh$ = new Subject<void>();
  private boardRequestId = 0;
  private loadedCardLimit = 20;
  private boardRequestInFlight = false;
  private filteredRefreshPending = false;

  columns: BoardColumn[] = [];
  failedDragActionColumns: BoardColumn[] = [];
  hasMultipleFailedDragActionColumns: boolean = false;
  boardColumnIds: string[] = [];
  footerDragActionDropZoneIds: string[] = [];
  cardsByColumn: Record<string, DynamicBoardCard[]> = {};
  managers: BoardManager[] = [];
  fields: BoardField[] = [];
  funnels: BoardFunnel[] = [];
  cardViewersById: Record<string, CardViewer[]> = {};
  funnelViewers: CardViewer[] = [];
  columnPagination: Record<string, { lastId: string | undefined; hasMore: boolean; loading: boolean }> = {};

  selectedCardIds: ReadonlySet<string> = new Set<string>();
  isBulkActionRunning: boolean = false;

  isCardViewSettingsSaving: boolean = false;
  activeCreateColumn: BoardColumn | null = null;
  isFunnelsLoading: boolean = false;
  isBoardLoading: boolean = false;
  isSavingFunnel: boolean = false;
  isSavingStages: boolean = false;
  isCardDragging: boolean = false;

  readonly FUNNEL_STATUS = FUNNEL_STATUS;
  readonly FUNNEL_TYPE = FUNNEL_TYPE;

  settingsMenuItems: Array<{ id?: string; label?: string; icon?: string; divider?: boolean }> = [
    { id: 'configure_fields', label: 'board_v3_settings_configure_fields', icon: 'settings' },
    { id: 'edit_stages', label: 'board_v3_settings_edit_stages', icon: 'view_column' },
    { id: 'warehouse', label: 'board_v3_settings_warehouse', icon: 'warehouse' },
    { id: 'automations', label: 'board_v3_settings_automations', icon: 'bolt' },
    { id: 'import_cards', label: 'board_v3_settings_import_cards', icon: 'download' },
    { id: 'export_csv', label: 'board_v3_settings_export_csv', icon: 'upload' },
    { id: 'find_duplicates', label: 'board_v3_settings_find_duplicates', icon: 'content_copy' },
    { id: 'reset_positions', label: 'board_v3_settings_reset_positions', icon: 'restart_alt' },
    { divider: true },
    { id: 'quick_create', label: 'board_v3_quick_create_title', icon: 'bolt' },
    { id: 'board_view', label: 'board_v3_settings_board_view', icon: 'view_kanban' },
    { id: 'archive_cards', label: 'board_v3_settings_archive_cards', icon: 'shopping_bag' },
  ];

  ngOnInit(): void {
    // Product items and their stock automation belong to deals, lead funnels have no warehouse settings
    if (this.type === FUNNEL_TYPE.LEAD) {
      this.settingsMenuItems = this.settingsMenuItems.filter((item) => item.id !== 'warehouse');
    }

    this.filteredRefresh$.pipe(auditTime(150), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        if (this.boardRequestInFlight) {
          this.filteredRefreshPending = true;
          return;
        }
        if (this.funnelId && this.hasAdvancedFilters) this.loadColumnsAndManagers(this.funnelId, true);
      });
    this.loadFunnels();
    this.openCardFromUrl();
    this.subscribeToChatUnreadUpdates();
    this.subscribeToConnectionRestored();
    this.subscribeToFunnelRealtime();
  }

  private subscribeToConnectionRestored(): void {
    this.realtimeService
      .connectionRestored$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.reloadCurrentFunnel();
      });
  }

  private subscribeToFunnelRealtime(): void {
    this.realtimeService
      .funnelCreated$(this.type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ funnel }) => {
        this.handleRealtimeFunnelUpsert(funnel);
      });

    this.realtimeService
      .funnelUpdated$(this.type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ funnel }) => {
        this.handleRealtimeFunnelUpsert(funnel);
      });

    this.realtimeService
      .funnelDeleted$(this.type)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(event => {
        this.handleRealtimeFunnelDelete(event);
      });
  }

  private subscribeToCardUpdates(funnelId: string): void {
    this.realtimeService
      .cardUpdated$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(({ card }) => {
        this.handleCardMoveUpdate(card);
        this.applyUpdatedCard(card);
      });
  }

  private subscribeToCardCreates(funnelId: string): void {
    this.realtimeService
      .cardCreated$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(({ card }) => {
        this.applyCreatedCard(card);
      });
  }

  private subscribeToCardDeletes(funnelId: string): void {
    this.realtimeService
      .cardDeleted$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(({ card_id, column_id }) => {
        if (this.hasAdvancedFilters) {
          this.filteredRefresh$.next();
          return;
        }
        this.removeCardFromLocalState(card_id, column_id);
      });
  }

  private subscribeToChatUnreadUpdates(): void {
    this.realtimeService
      .chatMessageNew$()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(({ chat }) => {
        this.applyChatUnreadUpdate(chat);
      });

    this.chatService.chatUnreadStateChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(chat => {
      this.applyChatUnreadUpdate(chat);
    });
  }

  private subscribeToColumnUpdates(funnelId: string): void {
    this.realtimeService
      .columnsUpdated$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(() => {
        if (this.funnelId === funnelId) {
          this.reloadCurrentFunnel();
        }
      });
  }

  private subscribeToImportProgress(funnelId: string): void {
    this.realtimeService
      .importProgress$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(event => this.handleImportProgress(event));
  }

  private handleImportProgress(data: ImportProgressEvent): void {
    switch (data.status) {
      case 'downloading':
      case 'parsing':
        this.notificationService.showInfo(
          '',
          data.message || this.translateService.instant('board_v3_import_processing'),
          0,
          true,
        );
        break;

      case 'processing':
        if (data.progress && data.current && data.total) {
          this.notificationService.showInfo(
            '',
            data.message ||
              this.translateService.instant('board_v3_import_progress', {
                current: data.current,
                total: data.total,
                progress: data.progress,
              }),
              0,
              true,
          );
        }
        break;

      case 'completed': {
        const successCount = data.result?.imported || 0;
        const errorCount = data.result?.failed || 0;
        const totalCount = data.result?.total_records || 0;

        if (!errorCount) {
          this.notificationService.showSuccess(
            '',
            this.translateService.instant('board_v3_import_success', { count: successCount }),
            0,
            true,
          );
        } else if (successCount && errorCount) {
          this.notificationService.showWarning(
            '',
            this.translateService.instant('board_v3_import_partial', {
              success: successCount,
              total: totalCount,
              errors: errorCount,
            }),
            0,
            true,
          );
        } else {
          this.notificationService.showError('', this.translateService.instant('board_v3_import_failed'),           0,
          true,);
        }

        this.reloadCurrentFunnel();
        break;
      }

      case 'failed':
        this.notificationService.showError(
          '',
          data.error?.message || data.message || this.translateService.instant('board_v3_import_failed'),
          0,
          true,
        );
        break;
    }
  }

  private subscribeToExportProgress(funnelId: string): void {
    this.realtimeService
      .exportProgress$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(event => this.handleExportProgress(event));
  }

  private subscribeToLeadConversionResults(funnelId: string): void {
    this.realtimeService
      .leadConversionResult$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(event => this.handleLeadConversionResult(event));
  }

  private handleExportProgress(data: ExportProgressEvent): void {
    switch (data.status) {
      case 'processing': {
        let message = '';
        switch (data.progress) {
          case 0:
            message = this.translateService.instant('board_v3_export_started');
            break;
          case 30:
            message = this.translateService.instant('board_v3_export_processing');
            break;
          case 70:
            message = this.translateService.instant('board_v3_export_uploading');
            break;
        }
        this.notificationService.showInfo('', message, 0, true);
        break;
      }

      case 'completed':
        this.notificationService.showSuccess('', this.translateService.instant('board_v3_export_success'), undefined, true);
        break;

      case 'failed':
        this.notificationService.showError('', this.translateService.instant('board_v3_export_failed'), undefined, true);
        break;
    }
  }

  private handleCardMoveUpdate(card: BoardCard): void {
    const pending = this.pendingMoveNotifications.get(card.id);
    if (!pending || pending.isLeadConversion || card.column_id !== pending.toColumnId) return;
    if (card.updated_by && !this.isCurrentUser(card.updated_by)) return;

    this.pendingMoveNotifications.delete(card.id);
    this.notificationService.showSuccess(
      '',
      this.translateService.instant('notifications_texts.success.successfully_moved'),
    );
  }

  private handleLeadConversionResult(event: LeadConversionResultEvent): void {
    if (!this.isCurrentUser(event.user_id)) return;

    if (event.batch_id && this.handleBulkLeadConversionResult(event)) return;

    const pending = this.pendingMoveNotifications.get(event.lead_card_id);
    if (!pending?.isLeadConversion) return;
    if (pending.dealFunnelId && pending.dealFunnelId !== event.deal_funnel_id) return;

    this.pendingMoveNotifications.delete(event.lead_card_id);

    if (event.status === 'completed') {
      this.notificationService.showSuccess('', this.translateService.instant('board_v3_lead_conversion_success'));
      return;
    }

    this.notificationService.showError(
      '',
      event.error?.message || this.translateService.instant('board_v3_lead_conversion_failed'),
    );
  }

  private handleBulkLeadConversionResult(event: LeadConversionResultEvent): boolean {
    const batchId = event.batch_id;
    if (!batchId) return false;

    const pending = this.pendingBulkLeadConversions.get(batchId);
    if (!pending) return false;
    if (pending.dealFunnelId !== event.deal_funnel_id) return true;

    pending.processedCount += 1;
    if (event.status === 'completed') {
      pending.movedCount += 1;
    } else {
      pending.failedCount += 1;
      if (event.reverted) pending.revertedCount += 1;
    }

    if (pending.processedCount >= pending.expectedCount) {
      this.completeBulkLeadConversion(batchId, pending);
    }

    return true;
  }

  private completeBulkLeadConversion(batchId: string, pending: PendingBulkLeadConversion): void {
    this.pendingBulkLeadConversions.delete(batchId);
    this.reloadCurrentFunnel();
    this.showBulkLeadConversionToast({
      affected: pending.expectedCount,
      moved_count: pending.movedCount,
      failed_count: pending.failedCount,
      reverted_count: pending.revertedCount,
    });
  }

  private isCurrentUser(userId: string | null | undefined): boolean {
    const currentUserId = this.baseService.currentUserInfo?.id ?? this.baseService.user?.id;
    return !!currentUserId && String(currentUserId) === String(userId);
  }

  ngOnDestroy(): void {
    if (this.funnelId) {
      this.realtimeService.unsubscribe(this.funnelId);
    }
    this.funnelRealtimeDestroy$.next();
    this.funnelRealtimeDestroy$.complete();
  }

  get totalCount(): number {
    return this.columns.reduce((sum, c) => sum + Number(c.count_cards || 0), 0);
  }

  get selectedFunnelCurrency(): string {
    return this.funnels.find(funnel => funnel.id === this.funnelId)?.currency || 'UAH';
  }

  protected get isMobile(): boolean {
    return this.baseService.isMobile;
  }

  onFieldsChanged(deletedFieldKey?: string | string[] | void): void {
    if (!this.funnelId) return;
    const deletedFieldKeys = Array.isArray(deletedFieldKey)
      ? deletedFieldKey
      : deletedFieldKey
        ? [deletedFieldKey]
        : [];
    for (const key of deletedFieldKeys) {
      this.removeDeletedFieldFromLocalState(key);
    }
    this.boardService
      .getFields(this.funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fields => (this.fields = fields || []));
  }

  get localCount(): number {
    return Object.values(this.cardsByColumn).reduce((sum, cards) => sum + cards.length, 0);
  }

  connectedTo(columnId: string): string[] {
    return [
      ...this.boardColumnIds.filter(id => id !== columnId),
      ...this.footerDragActionDropZoneIds,
    ];
  }

  cardsFor(columnId: string): DynamicBoardCard[] {
    return this.cardsByColumn[columnId] ?? [];
  }

  private setColumns(columns: BoardColumn[]): void {
    this.columns = columns;
    this.boardColumnIds = columns.map(column => column.id);
    this.footerDragActionDropZoneIds = boardV3DragActionDropZoneIds(columns);
    this.rebuildDragActionColumns();
  }

  private rebuildDragActionColumns(): void {
    this.failedDragActionColumns = this.columns.filter(column => column.type === 'FAILED');
    this.hasMultipleFailedDragActionColumns = this.failedDragActionColumns.length > 1;
  }

  onCardDragStart(): void {
    this.isCardDragging = true;
  }

  onCardDragEnd(): void {
    this.resetCardDragState();
  }

  private resetCardDragState(): void {
    this.isCardDragging = false;
  }

  onCardDrop(event: CdkDragDrop<DynamicBoardCard[]>, targetColumn: BoardColumn): void {
    const sameColumn = event.previousContainer === event.container;
    if (sameColumn && event.previousIndex === event.currentIndex) return;

    const card = event.item.data as DynamicBoardCard;
    if (!card) return;

    this.moveCardToColumn({
      card,
      fromColumnId: event.previousContainer.id,
      toColumnId: targetColumn.id,
      fromIndex: event.previousIndex,
      toIndex: event.currentIndex,
    });
  }

  onDragActionDrop(event: CdkDragDrop<DynamicBoardCard[]>, targetColumn: BoardColumn): void {
    this.resetCardDragState();

    const card = event.item.data as DynamicBoardCard;
    if (!card?.column_id) return;

    if (targetColumn.type === 'FAILED' && this.hasMultipleFailedDragActionColumns) {
      this.openFailedDragActionSidebar(card);
      return;
    }

    if (card.column_id === targetColumn.id) return;

    this.moveCardToColumn({
      card,
      fromColumnId: card.column_id,
      toColumnId: targetColumn.id,
      toIndex: 0,
    });
  }

  onDeleteDragActionDrop(event: CdkDragDrop<DynamicBoardCard[]>): void {
    this.resetCardDragState();

    const card = event.item.data as DynamicBoardCard;
    if (!card?.id) return;

    this.onDeleteCard(card);
  }

  private openFailedDragActionSidebar(card: DynamicBoardCard): void {
    this.openMoveCardSidebarWithColumns(card, this.failedDragActionColumns);
  }

  private openMoveCardSidebarWithColumns(card: DynamicBoardCard, columns: BoardColumn[]): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3MoveCard,
          width: '420px',
        },
        {
          card,
          columns,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const columnId = (result as { columnId?: string } | undefined)?.columnId;
        if (!columnId) return;

        this.onCardMove({ card, columnId });
      });
  }

  onCardMove({ card, columnId }: BoardCardMoveRequest): void {
    if (!card?.column_id) return;

    this.moveCardToColumn({
      card,
      fromColumnId: card.column_id,
      toColumnId: columnId,
      toIndex: 0,
    });
  }

  openMoveCardSidebar(card: DynamicBoardCard): void {
    if (!card?.id) return;

    this.openMoveCardSidebarWithColumns(card, this.columns);
  }

  private commitColumns(columnIds: string[]): void {
    const next = { ...this.cardsByColumn };
    for (const id of columnIds) {
      next[id] = [...(this.cardsByColumn[id] ?? [])];
    }
    this.cardsByColumn = next;
  }

  private moveCardToColumn(options: MoveCardOptions): void {
    const { card, fromColumnId, toColumnId, fromIndex, toIndex, leadConversion } = options;
    if (!card?.id || !fromColumnId || !toColumnId || this.movingCardIds.has(card.id)) return;
    const targetColumn = this.columns.find(column => column.id === toColumnId);
    if (!targetColumn) return;

    if (this.shouldConfirmLeadConversion(options, targetColumn)) {
      this.openLeadConversionSidebar(options);
      return;
    }

    const sameColumn = fromColumnId === toColumnId;
    const sourceCards = this.cardsByColumn[fromColumnId] ?? [];
    const targetCards = sameColumn ? sourceCards : (this.cardsByColumn[toColumnId] ?? []);
    const sourceIndex = fromIndex ?? sourceCards.findIndex(item => item.id === card.id);
    if (sourceIndex < 0) return;

    const previousSourceCards = [...sourceCards];
    const previousTargetCards = sameColumn ? previousSourceCards : [...targetCards];
    const previousColumns = this.columns;
    const previousColumnId = card.column_id;
    const previousPosition = card.position;
    const nextCardsByColumn = { ...this.cardsByColumn };
    let nextTargetCards: DynamicBoardCard[];
    let nextTargetIndex: number;

    if (sameColumn) {
      nextTargetCards = [...sourceCards];
      nextTargetIndex = this.clampIndex(toIndex, nextTargetCards.length - 1);
      if (sourceIndex === nextTargetIndex) return;

      moveItemInArray(nextTargetCards, sourceIndex, nextTargetIndex);
      nextCardsByColumn[toColumnId] = nextTargetCards;
    } else {
      const nextSourceCards = [...sourceCards];
      nextTargetCards = [...targetCards];
      const [movedCard] = nextSourceCards.splice(sourceIndex, 1);
      if (!movedCard) return;

      movedCard.column_id = toColumnId;
      nextTargetIndex = this.clampIndex(toIndex, nextTargetCards.length);
      nextTargetCards.splice(nextTargetIndex, 0, movedCard);
      nextCardsByColumn[fromColumnId] = nextSourceCards;
      nextCardsByColumn[toColumnId] = nextTargetCards;
    }

    this.movingCardIds.add(card.id);
    this.pendingMoveNotifications.set(card.id, {
      toColumnId,
      isLeadConversion: !!leadConversion,
      dealFunnelId: leadConversion?.deal_funnel_id,
    });
    this.cardsByColumn = nextCardsByColumn;

    if (!sameColumn) {
      this.updateColumnCounts(fromColumnId, -1);
      this.updateColumnCounts(toColumnId, +1);
      this.refillColumnIfNeeded(fromColumnId);
    }

    const topCard = nextTargetCards[nextTargetIndex - 1] ?? null;
    const bottomCard = nextTargetCards[nextTargetIndex + 1] ?? null;
    const payload: MoveCardPayload = {
      column_id: toColumnId,
      top_card_id: topCard?.id ?? null,
      bottom_card_id: bottomCard?.id ?? null,
      old_column_id: fromColumnId,
    };
    if (leadConversion) payload.lead_conversion = leadConversion;
    if (this.funnelId) payload.funnel_id = this.funnelId;

    this.boardService
      .moveCard(card.id, payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.movingCardIds.delete(card.id)),
      )
      .subscribe({
        next: response => {
          if (response?.position != null) card.position = response.position;
          if (response?.column_id && response.column_id !== card.column_id) {
            this.relocateCard(card, response.column_id);
          }
          if (this.hasAdvancedFilters) {
            this.filteredRefresh$.next();
            return;
          }
        },
        error: (error: unknown) => {
          this.pendingMoveNotifications.delete(card.id);
          card.column_id = previousColumnId;
          card.position = previousPosition;
          this.cardsByColumn = {
            ...this.cardsByColumn,
            [fromColumnId]: previousSourceCards,
            [toColumnId]: previousTargetCards,
          };
          this.setColumns(previousColumns);
          // A column that reserves or writes off product items refuses a card the stock can not cover
          const code = getApiError(error).code;
          this.notificationService.showError(
            '',
            code === 'INSUFFICIENT_STOCK' || code === 'CARD_ITEM_WAREHOUSE_REQUIRED'
              ? this.warehouseService.errorMessage(error)
              : this.translateService.instant(
                  leadConversion ? 'board_v3_lead_conversion_failed' : 'board_v3_move_card_failed',
                ),
          );
        },
      });
  }

  private shouldConfirmLeadConversion(options: MoveCardOptions, targetColumn: BoardColumn): boolean {
    return (
      this.type === FUNNEL_TYPE.LEAD &&
      targetColumn.type === 'SUCCESS' &&
      options.fromColumnId !== options.toColumnId &&
      !options.leadConversion
    );
  }

  private openLeadConversionSidebar(options: MoveCardOptions): void {
    this.boardService
      .openLeadConversionSidebar([options.card.id])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: LeadConversionResult | undefined) => {
        if (!result?.deal_funnel_id) return;

        this.moveCardToColumn({
          ...options,
          leadConversion: {
            target_type: result.target_type,
            deal_funnel_id: result.deal_funnel_id,
          },
        });
      });
  }

  private clampIndex(index: number, maxIndex: number): number {
    return Math.max(0, Math.min(index, Math.max(0, maxIndex)));
  }

  private relocateCard(card: DynamicBoardCard, targetColumnId: string): void {
    const currentColumnId = card.column_id;
    if (currentColumnId === targetColumnId) return;

    const currentCards = this.cardsByColumn[currentColumnId] ?? [];
    const targetCards = this.cardsByColumn[targetColumnId] ?? [];

    const index = currentCards.findIndex(c => c.id === card.id);
    if (index < 0) return;

    currentCards.splice(index, 1);
    card.column_id = targetColumnId;
    targetCards.push(card);

    this.commitColumns([currentColumnId, targetColumnId]);
  }

  private removeDeletedFieldFromLocalState(fieldKey: string): void {
    this.fields = this.fields.filter(field => this.fieldKey(field) !== fieldKey);
    this.funnelViewFields = this.funnelViewFields.filter(key => key !== fieldKey);
    this.funnelCreateFields = this.funnelCreateFields.filter(key => key !== fieldKey);
    this.activeFilterData = {
      ...this.activeFilterData,
      conditions: this.activeFilterData.conditions.filter(condition => condition.key !== fieldKey),
    };
    this.filterChipName = this.createFilterChipName();
    this.funnels = this.funnels.map(funnel => {
      if (funnel.id !== this.funnelId) return funnel;
      return {
        ...funnel,
        view_fields: (funnel.view_fields ?? []).filter(key => key !== fieldKey),
        create_fields: (funnel.create_fields ?? []).filter(key => key !== fieldKey),
      };
    });
  }

  private fieldKey(field: BoardField): string {
    return String(field.identifier ?? field.id ?? '');
  }

  private loadFunnels(preferredFunnelId?: string): void {
    this.isFunnelsLoading = true;
    this.boardService
      .getFunnels({ type: this.type })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .pipe(finalize(() => (this.isFunnelsLoading = false)))
      .subscribe(funnels => {
        this.funnels = funnels ?? [];
        const savedFunnelId = localStorage.getItem(SELECTED_FUNNEL_STORAGE_KEY + this.type);
        const selectedFunnelId = preferredFunnelId ?? savedFunnelId ?? this.funnelId;
        const general = this.funnels.find(f => f.status === FUNNEL_STATUS.GENERAL);
        const funnel = this.funnels.find(f => f.id === selectedFunnelId) ?? general ?? this.funnels[0];

        this.applyFunnelSelection(funnel);
      });
  }

  private handleRealtimeFunnelUpsert(funnel: BoardFunnel | undefined): void {
    if (funnel?.id) {
      this.upsertFunnel(funnel);

      if (this.funnelId === funnel.id) {
        this.applyActiveFunnelMetadata(funnel);
      }
    }

    this.loadFunnels(this.funnelId ?? undefined);
  }

  private handleRealtimeFunnelDelete(event: FunnelDeletedEvent): void {
    const deletedFunnelId = event.funnel_id;
    if (!deletedFunnelId) return;

    const wasActive = this.funnelId === deletedFunnelId;
    this.funnels = this.funnels.filter(funnel => funnel.id !== deletedFunnelId);

    if (!wasActive) {
      this.loadFunnels(this.funnelId ?? undefined);
      return;
    }

    localStorage.removeItem(SELECTED_FUNNEL_STORAGE_KEY + this.type);
    this.notificationService.showWarning(
      '',
      String(this.translateService.instant('board_v3_funnel_deleted_by_another_user')),
    );

    const fallbackFunnel = this.getFallbackFunnel(this.funnels);
    this.applyFunnelSelection(fallbackFunnel);
    this.loadFunnels(fallbackFunnel?.id);
  }

  private subscribeToCardPresence(funnelId: string): void {
    this.realtimeService
      .cardPresence$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(({ card_id, viewers }) => {
        this.applyCardPresence(card_id, viewers ?? []);
      });
  }

  private subscribeToFunnelPresence(funnelId: string): void {
    this.realtimeService
      .funnelPresence$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(({ viewers }) => {
        this.funnelViewers = viewers ?? [];
      });
  }

  private applyFunnelSelection(funnel: BoardFunnel | undefined): void {
    if (!funnel) {
      if (this.funnelId) {
        this.realtimeService.unsubscribe(this.funnelId);
      }
      this.funnelRealtimeDestroy$.next();
      this.funnelId = null;
      this.boardRequestsCancelled$.next();
      this.funnelName = '';
      this.funnelViewFields = [];
      this.funnelCreateFields = [];
      this.resetActiveFilters();
      this.resetBoardData();
      this.isBoardLoading = false;
      localStorage.removeItem(SELECTED_FUNNEL_STORAGE_KEY + this.type);
      return;
    }

    const previousFunnelId = this.funnelId;
    const isSameFunnel = previousFunnelId === funnel.id;

    this.funnelId = funnel.id;
    this.applyActiveFunnelMetadata(funnel);
    localStorage.setItem(SELECTED_FUNNEL_STORAGE_KEY + this.type, funnel.id);

    if (isSameFunnel) return;

    if (previousFunnelId) {
      this.realtimeService.unsubscribe(previousFunnelId);
    }
    this.funnelRealtimeDestroy$.next();
    this.resetBoardData();
    this.resetActiveFilters();
    this.isBoardLoading = true;
    this.subscribeToCardCreates(funnel.id);
    this.subscribeToCardUpdates(funnel.id);
    this.subscribeToCardDeletes(funnel.id);
    this.subscribeToColumnUpdates(funnel.id);
    this.subscribeToFieldUpdates(funnel.id);
    this.subscribeToCardPresence(funnel.id);
    this.subscribeToFunnelPresence(funnel.id);
    this.subscribeToImportProgress(funnel.id);
    this.subscribeToExportProgress(funnel.id);
    this.subscribeToLeadConversionResults(funnel.id);
    this.addCurrentFunnelViewer();
    this.realtimeService.subscribe(funnel.id);
    this.startFunnelPresenceHeartbeat(funnel.id);
    this.loadColumnsAndManagers(funnel.id);
  }

  private applyActiveFunnelMetadata(funnel: BoardFunnel): void {
    this.funnelName = funnel.name;
    this.funnelViewFields = funnel.view_fields ?? [];
    this.funnelCreateFields = funnel.create_fields ?? [];
  }

  private subscribeToFieldUpdates(funnelId: string): void {
    this.realtimeService
      .fieldsUpdated$(funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe(event => this.applyFieldsUpdate(event));
  }

  private applyFieldsUpdate(event: BoardFieldsUpdatedEvent): void {
    if (this.funnelId !== event.funnel_id) return;

    this.fields = event.fields ?? [];
    if (event.funnel) {
      this.upsertFunnel(event.funnel);
      this.applyActiveFunnelMetadata(event.funnel);
    }
  }

  private upsertFunnel(funnel: BoardFunnel): void {
    const index = this.funnels.findIndex(item => item.id === funnel.id);
    if (index === -1) {
      this.funnels = [...this.funnels, funnel];
      return;
    }

    const next = [...this.funnels];
    next[index] = funnel;
    this.funnels = next;
  }

  private getFallbackFunnel(funnels: BoardFunnel[]): BoardFunnel | undefined {
    return funnels.find(funnel => funnel.status === FUNNEL_STATUS.GENERAL) ?? funnels[0];
  }

  private startFunnelPresenceHeartbeat(funnelId: string): void {
    this.presenceService
      .funnelHeartbeat$(funnelId, () => this.funnelId === funnelId)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.funnelRealtimeDestroy$))
      .subscribe();
  }

  private loadColumnsAndManagers(funnelId: string, preserveCards = false): void {
    // Invalidate the old request before cancellation runs its finalize callback.
    const requestId = ++this.boardRequestId;
    this.boardRequestsCancelled$.next();
    this.boardRequestInFlight = true;
    this.filteredRefreshPending = false;
    const limit = preserveCards
      ? Math.max(this.loadedCardLimit, ...Object.values(this.cardsByColumn).map(cards => cards.length))
      : 20;
    this.loadedCardLimit = limit;
    const filters = this.activeCardFilters();
    const filterMode = this.activeFilterMode;
    const filterSignature = this.activeFilterSignature;
    const isCurrentRequest = () => this.funnelId === funnelId && requestId === this.boardRequestId;
    forkJoin({
      columns: this.boardService.getColumns({
        config: {
          funnel_id: funnelId,
          is_view_success: this.activeFilterData.view.is_view_success,
          is_view_failed: this.activeFilterData.view.is_view_failed,
        },
        filters,
        filter_mode: filterMode,
      }),
      managers: preserveCards ? of(this.managers) : this.boardService.getManagers({ take: 500, page: 1 }),
      fields: preserveCards ? of(this.fields) : this.boardService.getFields(funnelId),
    })
      .pipe(
        filter(() => isCurrentRequest() && this.activeFilterSignature === filterSignature),
        switchMap(({ columns, managers, fields }) => {
          this.managers = managers || [];
          this.fields = fields;
          return this.boardService.getAllCards({
            funnel_id: funnelId,
            limit,
            ...(filters.length ? { filters } : {}),
            filter_mode: filterMode,
          }).pipe(map(cards => ({ columns: columns || [], cards })));
        }),
        catchError(() => EMPTY),
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.boardRequestsCancelled$),
        finalize(() => {
          if (requestId === this.boardRequestId) {
            this.isBoardLoading = false;
            this.finishBoardRequest();
          }
        }),
      )
      .subscribe(({ columns, cards }) => {
        if (!isCurrentRequest() || this.activeFilterSignature !== filterSignature) return;
        this.setColumns(columns);
        const managerById = new Map(this.managers.map(m => [m.id, m]));

        const grouped = this.createEmptyCardsByColumn();
        for (const card of cards || []) {
          const dynamic = this.toDynamicCard(card, managerById);
          if (!grouped[card.column_id]) grouped[card.column_id] = [];
          grouped[card.column_id].push(dynamic);
        }
        this.cardsByColumn = grouped;
        const visibleIds = new Set(Object.values(grouped).flat().map(card => card.id));
        this.selectedCardIds = new Set([...this.selectedCardIds].filter(id => visibleIds.has(id)));
        this.locallyRemovedCardIds.clear();

        this.columnPagination = this.columns.reduce<
          Record<string, { lastId: string | undefined; hasMore: boolean; loading: boolean }>
        >((acc, c) => {
          const cards = grouped[c.id] ?? [];
          acc[c.id] = { lastId: cards.at(-1)?.id, hasMore: cards.length >= limit, loading: false };
          return acc;
        }, {});
      });
  }

  onLoadMore(column: BoardColumn): void {
    if (!this.funnelId || this.boardRequestInFlight) return;
    const state = this.columnPagination[column.id];
    if (!state || !state.hasMore || state.loading) return;
    const funnelId = this.funnelId;
    const requestId = this.boardRequestId;
    const filters = this.activeCardFilters();
    const filterSignature = this.activeFilterSignature;

    this.columnPagination = {
      ...this.columnPagination,
      [column.id]: { ...state, loading: true },
    };

    this.boardService
      .getCards({
        column_id: column.id,
        limit: 20,
        last_id: state.lastId,
        ...(filters.length ? { filters } : {}),
        filter_mode: this.activeFilterMode,
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.boardRequestsCancelled$),
        finalize(() => {
          const current = this.columnPagination[column.id];
          if (this.funnelId === funnelId && current) {
            this.columnPagination = { ...this.columnPagination, [column.id]: { ...current, loading: false } };
          }
        }),
      )
      .subscribe({
        next: cards => {
          if (this.funnelId !== funnelId || requestId !== this.boardRequestId || this.activeFilterSignature !== filterSignature) return;
          const batch = cards ?? [];
          const managerById = new Map(this.managers.map(m => [m.id, m]));
          const newCards = batch.map(c => this.toDynamicCard(c, managerById));

          this.columnPagination = {
            ...this.columnPagination,
            [column.id]: {
              lastId: batch.at(-1)?.id ?? state.lastId,
              hasMore: batch.length >= 20,
              loading: false,
            },
          };

          if (newCards.length) {
            this.cardsByColumn = {
              ...this.cardsByColumn,
              [column.id]: [...(this.cardsByColumn[column.id] ?? []), ...newCards],
            };
          }
        },
        error: () => {
          if (this.funnelId !== funnelId || requestId !== this.boardRequestId || this.activeFilterSignature !== filterSignature) return;
          this.columnPagination = {
            ...this.columnPagination,
            [column.id]: { ...state, loading: false },
          };
        },
      });
  }

  private updateColumnCounts(columnId: string, delta: number): void {
    this.setColumns(
      this.columns.map(c =>
        c.id === columnId ? { ...c, count_cards: String(Math.max(0, Number(c.count_cards) + delta)) } : c,
      ),
    );
  }

  private finishBoardRequest(): void {
    this.boardRequestInFlight = false;
    if (this.filteredRefreshPending) {
      this.filteredRefreshPending = false;
      this.filteredRefresh$.next();
    }
  }

  private refillColumnIfNeeded(columnId: string): void {
    const state = this.columnPagination[columnId];
    if (!state || !state.hasMore || state.loading) return;
    if ((this.cardsByColumn[columnId]?.length ?? 0) >= 20) return;

    const column = this.columns.find(c => c.id === columnId);
    if (column) this.onLoadMore(column);
  }

  private toDynamicCard(card: BoardCard, managerById: Map<string, BoardManager>): DynamicBoardCard {
    return {
      ...card,
      created_by_user: card.created_by ? (managerById.get(card.created_by) ?? null) : null,
      updated_by_user: card.updated_by ? (managerById.get(card.updated_by) ?? null) : null,
      assigned_id_user: card.assigned_id ? (managerById.get(card.assigned_id) ?? null) : null,
      viewers: this.cardViewersById[card.id] ?? [],
    };
  }

  private applyCreatedCard(card: BoardCard): void {
    if (!card?.id) return;
    if (this.hasAdvancedFilters) {
      this.filteredRefresh$.next();
      return;
    }

    for (const cards of Object.values(this.cardsByColumn)) {
      if (cards.some(item => item.id === card.id)) return;
    }

    const targetCards = this.cardsByColumn[card.column_id];
    if (!targetCards) return;

    const managerById = new Map(this.managers.map(m => [m.id, m]));
    const createdCard = this.toDynamicCard(card, managerById);
    this.cardsByColumn = {
      ...this.cardsByColumn,
      [card.column_id]: this.sortCardsByPosition([createdCard, ...targetCards]),
    };
    this.updateColumnCounts(card.column_id, +1);
  }

  private applyUpdatedCard(card: BoardCard): void {
    if (!card?.id) return;
    if (this.hasAdvancedFilters) {
      this.filteredRefresh$.next();
      return;
    }

    let currentColumnId: string | null = null;
    let currentIdx = -1;
    for (const [colId, cards] of Object.entries(this.cardsByColumn)) {
      const idx = cards.findIndex(c => c.id === card.id);
      if (idx !== -1) {
        currentColumnId = colId;
        currentIdx = idx;
        break;
      }
    }

    const targetColumnId = card.column_id;
    const managerById = new Map(this.managers.map(m => [m.id, m]));
    const updatedCard = this.toDynamicCard(card, managerById);
    if (currentColumnId === null) {
      const targetCol = this.cardsByColumn[targetColumnId];
      if (!targetCol) return;

      const pagination = this.columnPagination[targetColumnId];
      const lastPosition = targetCol.at(-1)?.position;
      const fitsInRange =
        !pagination?.hasMore || lastPosition == null || this.comparePositions(card.position, lastPosition) <= 0;
      if (!fitsInRange) return;

      const toCards = this.sortCardsByPosition([...targetCol, updatedCard]);
      this.cardsByColumn = { ...this.cardsByColumn, [targetColumnId]: toCards };
      this.updateColumnCounts(targetColumnId, +1);
      return;
    }

    if (currentColumnId !== targetColumnId) {
      const fromCards = [...(this.cardsByColumn[currentColumnId] ?? [])];
      fromCards.splice(currentIdx, 1);

      const destCards = this.cardsByColumn[targetColumnId] ?? [];
      const destPagination = this.columnPagination[targetColumnId];
      const lastDestPosition = destCards.at(-1)?.position;
      const fitsInDest =
        !destPagination?.hasMore ||
        lastDestPosition == null ||
        this.comparePositions(card.position, lastDestPosition) <= 0;

      if (fitsInDest) {
        const toCards = this.sortCardsByPosition([...destCards, updatedCard]);
        this.cardsByColumn = { ...this.cardsByColumn, [currentColumnId]: fromCards, [targetColumnId]: toCards };
      } else {
        this.cardsByColumn = { ...this.cardsByColumn, [currentColumnId]: fromCards };
      }

      this.updateColumnCounts(currentColumnId, -1);
      this.updateColumnCounts(targetColumnId, +1);
      return;
    }

    const colCards = this.cardsByColumn[currentColumnId];
    const next = [...colCards];
    next[currentIdx] = updatedCard;
    this.cardsByColumn = { ...this.cardsByColumn, [currentColumnId]: this.sortCardsByPosition(next) };
  }

  private applyChatUnreadUpdate(chatUpdate?: ChatUnreadState | null): void {
    const chatId = chatUpdate?.id;
    if (!chatId) return;

    const hasUnreadCount = 'unread_count' in chatUpdate;
    const hasMarkedUnread = 'is_mark_unread' in chatUpdate;
    if (!hasUnreadCount && !hasMarkedUnread) return;
    if (this.hasAdvancedFilters) {
      this.filteredRefresh$.next();
      return;
    }

    const unreadCount = hasUnreadCount ? this.normalizeUnreadCount(chatUpdate.unread_count) : undefined;
    const isMarkedUnread = hasMarkedUnread ? !!chatUpdate.is_mark_unread : undefined;
    let hasChanges = false;

    const nextCardsByColumn = Object.entries(this.cardsByColumn).reduce<Record<string, DynamicBoardCard[]>>(
      (acc, [columnId, cards]) => {
        let hasColumnChanges = false;
        const nextCards = cards.map(card => {
          const cardChats = card.chats ?? [];
          if (!cardChats.some(chat => chat.id === chatId)) return card;

          let hasCardChanges = false;
          const nextChats = cardChats.map(chat => {
            if (chat.id !== chatId) return chat;

            const nextChat = {
              ...chat,
              ...(unreadCount !== undefined ? { unread_count: unreadCount } : {}),
              ...(isMarkedUnread !== undefined ? { is_mark_unread: isMarkedUnread } : {}),
            };

            hasCardChanges =
              hasCardChanges ||
              nextChat.unread_count !== chat.unread_count ||
              nextChat.is_mark_unread !== chat.is_mark_unread;

            return nextChat;
          });

          if (!hasCardChanges) return card;

          hasColumnChanges = true;
          return {
            ...card,
            chats: nextChats,
          };
        });

        acc[columnId] = hasColumnChanges ? nextCards : cards;
        hasChanges = hasChanges || hasColumnChanges;
        return acc;
      },
      {},
    );

    if (hasChanges) {
      this.cardsByColumn = nextCardsByColumn;
    }
  }

  private normalizeUnreadCount(count: number | null | undefined): number {
    const unreadCount = Number(count ?? 0);
    if (!Number.isFinite(unreadCount)) return 0;

    return Math.max(0, unreadCount);
  }

  private sortCardsByPosition(cards: DynamicBoardCard[]): DynamicBoardCard[] {
    return [...cards].sort((a, b) => this.comparePositions(a.position, b.position));
  }

  private comparePositions(a: unknown, b: unknown): number {
    const left = String(a ?? '');
    const right = String(b ?? '');
    if (left === right) return 0;
    return left < right ? -1 : 1;
  }

  private activeCardFilters(): BoardFilterCondition[] {
    return this.activeFilterData.conditions;
  }

  private get activeFilterMode(): BoardFilterMode {
    return this.activeFilterData.mode === 'OR' ? 'OR' : 'AND';
  }

  private get activeFilterSignature(): string {
    return JSON.stringify(this.activeFilterData);
  }

  private resetActiveFilters(): void {
    this.activeFilterId = null;
    this.activeFilterData = normalizeBoardFilterData();
    this.activeFilterName = '';
    this.filterChipName = '';
  }

  private get hasAdvancedFilters(): boolean {
    return this.activeFilterData.conditions.length > 0;
  }

  get hasActiveFilters(): boolean {
    return this.hasAdvancedFilters
      || this.activeFilterData.view.is_view_success
      || this.activeFilterData.view.is_view_failed;
  }

  onCreate(): void {}

  onSelectFunnel(funnel: BoardFunnel): void {
    this.applyFunnelSelection(funnel);
  }

  onCreateFunnel(): void {
    this.openCreateFunnelSidebar(null);
  }

  onEditFunnel(funnel: BoardFunnel): void {
    this.openCreateFunnelSidebar(funnel);
  }

  onCreateColumn(): void {
    this.openStageSettings();
  }

  private openCreateFunnelSidebar(funnel: BoardFunnel | null): void {
    this.stackedSidebarService
      .openSidebar({ type: SidebarType.BoardV3CreateFunnel, width: '480px' }, { funnel, funnelType: this.type })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result) => {
        const savedFunnel = (result as { funnel?: BoardFunnel } | undefined)?.funnel;
        if (savedFunnel) this.loadFunnels(savedFunnel.id);
      });
  }

  onDeleteFunnel(funnel: BoardFunnel): void {
    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_delete_funnel_confirm', { name: funnel.name }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) return;

        this.isSavingFunnel = true;
        this.boardService
          .deleteFunnel(funnel.id)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.isSavingFunnel = false)),
          )
          .subscribe(() => {
            if (this.funnelId === funnel.id) {
              localStorage.removeItem(SELECTED_FUNNEL_STORAGE_KEY + this.type);
            }
            this.loadFunnels();
          });
      });
  }

  onSettings(): void {}

  openFiltersSidebar(): void {
    if (!this.funnelId || this.isBoardLoading) return;

    this.stackedSidebarService
      .openSidebar<BoardV3FiltersSidebarResult>(
        {
          type: SidebarType.BoardV3Filters,
          width: '980px',
          maxWidth: '90vw',
        },
        {
          funnelId: this.funnelId,
          funnelType: this.type,
          fields: this.fields,
          managers: this.managers,
          activeData: this.activeFilterData,
          activeFilterId: this.activeFilterId,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const filters = result?.filters;
        if (!filters) return;
        this.applyBoardFilters(filters, result?.filterName ?? '', result?.filterId ?? null);
      });
  }

  private applyBoardFilters(filters: BoardFilterSetData, filterName: string, filterId: string | null): void {
    this.activeFilterId = filterId;
    this.activeFilterData = normalizeBoardFilterData(filters);
    this.activeFilterName = filterName;
    this.filterChipName = this.createFilterChipName();
    this.reloadCurrentFunnel(true);
  }

  private createFilterChipName(): string {
    const conditionsCount = this.activeFilterData.conditions.length;
    const viewCount = Number(this.activeFilterData.view.is_view_success) + Number(this.activeFilterData.view.is_view_failed);
    const count = conditionsCount + viewCount;
    return count ? String(this.translateService.instant('board_v3_filter.active_count', { count })) : '';
  }

  onOpenChat(chat: BoardCardChat): void {
    if (!chat?.id) return;

    this.stackedSidebarService.openChats(chat.id).subscribe();
  }

  onSettingsAction(id: string): void {
    if (id === 'board_view') {
      this.openCardViewSettingsSidebar();
    } else if (id === 'quick_create') {
      this.openQuickCreateSettingsSidebar();
    } else if (id === 'configure_fields') {
      this.openFieldsSettingsSidebar();
    } else if (id === 'edit_stages') {
      this.openStageSettings();
    } else if (id === 'warehouse') {
      this.openWarehouseSettings();
    } else if (id === 'automations') {
      this.openAutomations();
    } else if (id === 'import_cards') {
      this.openImportLeads();
    } else if (id === 'export_csv') {
      this.openExportFields();
    } else if (id === 'find_duplicates') {
      this.findDuplicates();
    } else if (id === 'reset_positions') {
      this.resetCardPositions();
    }
  }

  private resetCardPositions(): void {
    const funnelId = this.funnelId;
    if (!funnelId || this.isResettingPositions) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_reset_positions_confirm'),
        isTranslate: true,
        checkbox: { label: 'board_v3_reset_positions_sort_by_created' },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) return;

        const sortByCreatedAt = typeof result === 'object' && result.checkbox_value === true;
        this.isResettingPositions = true;
        this.boardService
          .resetCardPositions(funnelId, sortByCreatedAt)
          .pipe(
            finalize(() => (this.isResettingPositions = false)),
            takeUntilDestroyed(this.destroyRef),
          )
          .subscribe(() => {
            this.notificationService.showSuccess('', this.translateService.instant('board_v3_reset_positions_success'));
            if (this.funnelId === funnelId) this.reloadCurrentFunnel();
          });
      });
  }

  /**
   * Opens the merge sidebar for the first group of duplicates in the funnel.
   * After a successful merge it looks for the next group, until there are none or the sidebar is closed without merging.
   */
  private findDuplicates(isNextGroup = false): void {
    const funnelId = this.funnelId;
    if (!funnelId || this.isSearchingDuplicates) return;

    this.isSearchingDuplicates = true;
    this.boardService
      .findDuplicateCards(funnelId)
      .pipe(
        finalize(() => (this.isSearchingDuplicates = false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (cards) => {
          if (cards.length < 2) {
            this.notificationService.showInfo(
              '',
              this.translateService.instant(
                isNextGroup ? 'board_v3_duplicates_no_more' : 'board_v3_duplicates_not_found',
              ),
            );
            return;
          }

          this.boardService
            .openMergeCardsSidebar(cards.map((card) => card.id))
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((result) => {
              if (!result?.target_id) return;

              this.reloadCurrentFunnel();
              if (this.funnelId === funnelId) this.findDuplicates(true);
            });
        },
      });
  }

  openCardViewSettingsSidebar(): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3CardViewSettings,
          width: '400px',
        },
        {
          funnelId: this.funnelId,
          fields: this.fields,
          activeKeys: this.funnelViewFields,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const keys = (result as { viewFields?: string[] } | undefined)?.viewFields;
        if (!Array.isArray(keys)) return;
        this.onApplyViewFields(keys);
      });
  }

  openQuickCreateSettingsSidebar(): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3QuickCreateSettings,
          width: '400px',
        },
        {
          funnelId: this.funnelId,
          activeKeys: this.funnelCreateFields,
          fields: this.fields,
          managers: this.managers,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const keys = (result as { createFields?: string[] } | undefined)?.createFields;
        if (!Array.isArray(keys)) return;
        this.onApplyQuickCreateFields(keys);
      });
  }

  private openFieldsSettingsSidebar(): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3FieldsSettings,
          width: '90vw',
          maxWidth: '90vw',
        },
        {
          funnelId: this.funnelId,
          funnelCurrency: this.selectedFunnelCurrency,
          entityType: 'FUNNEL',
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const sidebarResult = result as { changed?: boolean; deletedFieldKeys?: string[] } | undefined;
        if (!sidebarResult?.changed) return;
        this.onFieldsChanged(sidebarResult.deletedFieldKeys);
      });
  }

  onApplyViewFields(keys: string[]): void {
    if (!this.funnelId || this.isCardViewSettingsSaving) return;
    this.isCardViewSettingsSaving = true;
    this.boardService
      .updateFunnel(this.funnelId, { view_fields: keys })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isCardViewSettingsSaving = false)),
      )
      .subscribe({
        next: funnel => {
          this.funnelViewFields = funnel?.view_fields ?? keys;
        },
      });
  }

  onApplyQuickCreateFields(keys: string[]): void {
    if (!this.funnelId) return;
    this.boardService
      .updateFunnel(this.funnelId, { create_fields: keys })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(funnel => {
        this.funnelCreateFields = funnel?.create_fields ?? keys;
      });
  }

  onColumnAdd(column: BoardColumn): void {
    this.activeCreateColumn = column;
  }

  onQuickCardCreated(card: BoardCard): void {
    if (this.hasAdvancedFilters) {
      this.filteredRefresh$.next();
      return;
    }

    const managerById = new Map(this.managers.map(m => [m.id, m]));
    const dynamic = this.toDynamicCard(card, managerById);
    const existing = this.cardsByColumn[card.column_id] ?? [];
    this.cardsByColumn = { ...this.cardsByColumn, [card.column_id]: [dynamic, ...existing] };
    this.updateColumnCounts(card.column_id, +1);
  }

  onColumnMore(column: BoardColumn): void {
    this.openStageEditor(column);
  }

  onColumnClear(column: BoardColumn): void {
    if (this.isBulkActionRunning) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_clear_column_confirm', { name: column.name }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) return;

        this.clearColumn(column);
      });
  }

  private clearColumn(column: BoardColumn): void {
    this.isBulkActionRunning = true;
    const removedIds = new Set((this.cardsByColumn[column.id] ?? []).map(card => card.id));

    this.boardService
      .massAction({ action: 'DELETE', ids: [], column_id: column.id })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isBulkActionRunning = false)),
      )
      .subscribe({
        next: () => {
          this.cardsByColumn = { ...this.cardsByColumn, [column.id]: [] };
          this.columnPagination = {
            ...this.columnPagination,
            [column.id]: { lastId: undefined, hasMore: false, loading: false },
          };
          this.setColumns(this.columns.map(c => (c.id === column.id ? { ...c, count_cards: '0' } : c)));

          if (removedIds.size) {
            const nextSelected = new Set([...this.selectedCardIds].filter(id => !removedIds.has(id)));
            if (nextSelected.size !== this.selectedCardIds.size) this.selectedCardIds = nextSelected;
          }
        },
        error: () => {
          this.notificationService.showError('', this.translateService.instant('board_v3_clear_column_failed'));
        },
      });
  }

  onDeleteCard(card: DynamicBoardCard): void {
    if (!card?.id) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_delete_card_confirm', { name: card.name }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) return;

        this.boardService
          .deleteCard(card.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(() => {
            this.removeCardFromLocalState(card.id, card.column_id);
            this.notificationService.showSuccess(
              '',
              this.translateService.instant('notifications_texts.success.successfully_deleted'),
            );
          });
      });
  }

  get isSelectionMode(): boolean {
    return this.selectedCardIds.size > 0;
  }

  get selectedCount(): number {
    return this.selectedCardIds.size;
  }

  get selectedCards(): DynamicBoardCard[] {
    return Object.values(this.cardsByColumn)
      .flat()
      .filter(card => this.selectedCardIds.has(card.id));
  }

  onCardSelectionToggle(card: DynamicBoardCard): void {
    if (!card?.id) return;

    const next = new Set(this.selectedCardIds);
    if (next.has(card.id)) {
      next.delete(card.id);
    } else {
      next.add(card.id);
    }
    this.selectedCardIds = next;
  }

  isColumnFullySelected(columnId: string): boolean {
    const cards = this.cardsFor(columnId);
    return cards.length > 0 && cards.every(card => this.selectedCardIds.has(card.id));
  }

  isColumnPartiallySelected(columnId: string): boolean {
    return this.cardsFor(columnId).some(card => this.selectedCardIds.has(card.id));
  }

  onToggleColumnSelection(column: BoardColumn): void {
    const cards = this.cardsFor(column.id);
    if (!cards.length) return;

    const next = new Set(this.selectedCardIds);
    if (this.isColumnFullySelected(column.id)) {
      for (const card of cards) next.delete(card.id);
    } else {
      for (const card of cards) next.add(card.id);
    }
    this.selectedCardIds = next;
  }

  clearSelection(): void {
    if (!this.selectedCardIds.size) return;
    this.selectedCardIds = new Set<string>();
  }

  get canMergeSelectedCards(): boolean {
    return this.selectedCount > 1;
  }

  openMergeSelectedCardsSidebar(): void {
    const cards = this.selectedCards;
    if (cards.length < 2 || this.isBulkActionRunning) return;

    this.boardService
      .openMergeCardsSidebar(cards.map(card => card.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result?.target_id) return;

        for (const cardId of result.ids.filter(id => id !== result.target_id)) {
          const card = cards.find(item => item.id === cardId);
          this.removeCardFromLocalState(cardId, card?.column_id);
        }
        this.clearSelection();
        this.reloadCurrentFunnel();
      });
  }

  openMoveSelectedCardsSidebar(): void {
    const cards = this.selectedCards;
    if (!cards.length || this.isBulkActionRunning) return;

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3MoveCard,
          width: '420px',
        },
        {
          card: cards.length === 1 ? cards[0] : null,
          cards,
          columns: this.columns,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const columnId = (result as { columnId?: string } | undefined)?.columnId;
        if (!columnId) return;

        const targetColumn = this.columns.find(column => column.id === columnId);
        if (targetColumn && this.shouldConfirmBulkLeadConversion(cards, targetColumn)) {
          this.openBulkLeadConversionSidebar(cards, columnId);
          return;
        }

        this.moveSelectedCards(cards, columnId);
      });
  }

  private moveSelectedCards(
    cards: DynamicBoardCard[],
    columnId: string,
    leadConversion?: LeadConversionRequest,
  ): void {
    if (this.isBulkActionRunning || !this.columns.some(column => column.id === columnId)) return;

    const cardsToMove = cards.filter(card => card.column_id !== columnId);
    if (!cardsToMove.length) {
      this.clearSelection();
      return;
    }

    const bulkLeadConversion = leadConversion
      ? {
          ...leadConversion,
          batch_id: leadConversion.batch_id || this.createBulkLeadConversionBatchId(),
        }
      : undefined;

    const previousCardsByColumn = this.cardsByColumn;
    const previousColumns = this.columns;

    const nextCardsByColumn = { ...this.cardsByColumn };
    const columnDeltas = new Map<string, number>();
    for (const card of cardsToMove) {
      nextCardsByColumn[card.column_id] = (nextCardsByColumn[card.column_id] ?? []).filter(c => c.id !== card.id);
      columnDeltas.set(card.column_id, (columnDeltas.get(card.column_id) ?? 0) - 1);
      columnDeltas.set(columnId, (columnDeltas.get(columnId) ?? 0) + 1);
    }
    const movedCards = cardsToMove.map(card => ({ ...card, column_id: columnId }));
    nextCardsByColumn[columnId] = [...movedCards, ...(nextCardsByColumn[columnId] ?? [])];

    this.cardsByColumn = nextCardsByColumn;
    for (const [colId, delta] of columnDeltas) this.updateColumnCounts(colId, delta);
    for (const fromColumnId of new Set(cardsToMove.map(card => card.column_id))) {
      this.refillColumnIfNeeded(fromColumnId);
    }

    if (bulkLeadConversion?.batch_id) {
      this.pendingBulkLeadConversions.set(bulkLeadConversion.batch_id, {
        dealFunnelId: bulkLeadConversion.deal_funnel_id,
        expectedCount: cardsToMove.length,
        processedCount: 0,
        movedCount: 0,
        failedCount: 0,
        revertedCount: 0,
      });
    }

    this.isBulkActionRunning = true;
    this.boardService
      .massAction({
        action: 'MOVE',
        ids: cardsToMove.map(card => card.id),
        payload: {
          column_id: columnId,
          ...(bulkLeadConversion ? { lead_conversion: bulkLeadConversion } : {}),
        },
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.isBulkActionRunning = false)),
      )
      .subscribe({
        next: summary => {
          this.clearSelection();
          if (bulkLeadConversion?.batch_id) {
            this.applyBulkLeadConversionImmediateResult(bulkLeadConversion.batch_id, summary);
          } else {
            this.notificationService.showSuccess(
              '',
              this.translateService.instant('notifications_texts.success.successfully_moved'),
            );
          }
        },
        error: () => {
          if (bulkLeadConversion?.batch_id) {
            this.pendingBulkLeadConversions.delete(bulkLeadConversion.batch_id);
          }
          this.cardsByColumn = previousCardsByColumn;
          this.setColumns(previousColumns);
          this.notificationService.showError(
            '',
            this.translateService.instant(
              bulkLeadConversion ? 'board_v3_lead_conversion_bulk_failed' : 'board_v3_move_cards_failed',
              {
                moved: 0,
                failed: cardsToMove.length,
                reverted: 0,
              },
            ),
          );
        },
      });
  }

  private applyBulkLeadConversionImmediateResult(batchId: string, summary: BoardMassActionResponse): void {
    const pending = this.pendingBulkLeadConversions.get(batchId);
    if (!pending) return;

    const immediateFailed = summary.failed_count ?? 0;
    if (immediateFailed > 0) {
      pending.failedCount += immediateFailed;
      pending.revertedCount += summary.reverted_count ?? 0;
      pending.processedCount += immediateFailed;
      this.reloadCurrentFunnel();
    }

    if ((summary.queued_count ?? summary.moved_count ?? 0) === 0 || pending.processedCount >= pending.expectedCount) {
      this.completeBulkLeadConversion(batchId, pending);
    }
  }

  private shouldConfirmBulkLeadConversion(cards: DynamicBoardCard[], targetColumn: BoardColumn): boolean {
    return (
      this.type === FUNNEL_TYPE.LEAD &&
      targetColumn.type === 'SUCCESS' &&
      cards.some(card => card.column_id !== targetColumn.id)
    );
  }

  private openBulkLeadConversionSidebar(cards: DynamicBoardCard[], columnId: string): void {
    const cardsToMove = cards.filter(card => card.column_id !== columnId);
    if (!cardsToMove.length) {
      this.clearSelection();
      return;
    }

    this.boardService.openLeadConversionSidebar(cardsToMove.map(card => card.id))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((result: LeadConversionResult | undefined) => {
        if (!result?.deal_funnel_id) return;

        this.moveSelectedCards(cardsToMove, columnId, {
          target_type: result.target_type,
          deal_funnel_id: result.deal_funnel_id,
        });
      });
  }

  private createBulkLeadConversionBatchId(): string {
    const cryptoApi = globalThis.crypto;
    if (cryptoApi?.randomUUID) return cryptoApi.randomUUID();

    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, char => {
      const randomValue = Math.floor(Math.random() * 16);
      const value = char === 'x' ? randomValue : (randomValue % 4) + 8;
      return value.toString(16);
    });
  }

  private showBulkLeadConversionToast(summary: BoardMassActionResponse): void {
    const moved = summary.moved_count ?? 0;
    const failed = summary.failed_count ?? 0;
    const reverted = summary.reverted_count ?? 0;
    const params = { moved, failed, reverted };

    if (failed === 0) {
      this.notificationService.showSuccess(
        '',
        this.translateService.instant('board_v3_lead_conversion_bulk_success', params),
      );
    } else if (moved > 0) {
      this.notificationService.showWarning(
        '',
        this.translateService.instant('board_v3_lead_conversion_bulk_partial', params),
      );
    } else {
      this.notificationService.showError(
        '',
        this.translateService.instant('board_v3_lead_conversion_bulk_failed', params),
      );
    }
  }

  onDeleteSelectedCards(): void {
    const cards = this.selectedCards;
    if (!cards.length || this.isBulkActionRunning) return;

    this.dialogService
      .openConfirm({
        text: this.translateService.instant('board_v3_delete_cards_confirm', { count: cards.length }),
        isTranslate: true,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if (!result) return;

        this.isBulkActionRunning = true;
        this.boardService
          .massAction({ action: 'DELETE', ids: cards.map(card => card.id) })
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.isBulkActionRunning = false)),
          )
          .subscribe({
            next: () => {
              for (const card of cards) {
                this.removeCardFromLocalState(card.id, card.column_id);
              }
              this.clearSelection();
            },
            error: () => {
              this.notificationService.showError('', this.translateService.instant('board_v3_delete_cards_failed'));
            },
          });
      });
  }

  openStageSettings(): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3StageSettings,
          width: '640px',
        },
        {
          funnelId: this.funnelId,
          columns: this.copyColumnsForSettings(this.columns),
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        if ((result as { changed?: boolean } | undefined)?.changed) {
          this.reloadCurrentFunnel();
        }
      });
  }

  /** Reserve and write-off columns of the funnel and the default warehouse of card items */
  openWarehouseSettings(): void {
    if (!this.funnelId) return;

    this.warehouseService.openFunnelSettings(this.funnelId).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
  }

  private openStageEditor(column: BoardColumn): void {
    if (!this.funnelId || this.isSavingStages) return;

    const initialColor: BoardColumnColor = column.color;
    this.modalService
      .openModal({
        title: 'change_column',
        translate: true,
        component: BoardColumnEditComponent,
        width: '420px',
        data: {
          column: {
            name: column.name,
            color: initialColor,
            type: column.type,
          },
          canEditType: false,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        const updatedColumn = result?.updatedColumn;
        const name = updatedColumn?.name?.trim();
        if (!name || !this.funnelId) return;

        const color: BoardColumnColor = {
          background: updatedColumn.bg_color,
          text: updatedColumn.text_color,
        };
        this.isSavingStages = true;
        this.boardService
          .updateColumn(column.id, { name, color })
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            finalize(() => (this.isSavingStages = false)),
          )
          .subscribe({
            next: () => this.reloadCurrentFunnel(),
            error: () => this.openStageSettings(),
          });
      });
  }

  private reloadCurrentFunnel(showLoading: boolean = false): void {
    if (!this.funnelId) return;
    if (showLoading) {
      this.resetBoardData();
      this.isBoardLoading = true;
    }
    this.loadColumnsAndManagers(this.funnelId);
  }

  openAutomations(): void {
    this.router.navigate(['/crm/automation'], { queryParams: { tab: AutomationTab.CARD } });
  }

  openImportLeads(): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3ImportMapping,
          width: '640px',
        },
        {
          funnelId: this.funnelId,
          fields: this.fields,
          columns: this.columns,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  openExportFields(): void {
    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.BoardV3ExportFields,
          width: '480px',
        },
        {
          funnelId: this.funnelId,
          fields: this.fields,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private copyColumnsForSettings(columns: BoardColumn[]): BoardColumn[] {
    return columns.map(column => ({
      ...column,
      color: { ...column.color },
    }));
  }

  private resetBoardData(): void {
    this.setColumns([]);
    this.resetCardsForCurrentColumns();
    this.cardViewersById = {};
    this.funnelViewers = [];
    this.fields = [];
    this.managers = [];
    this.activeCreateColumn = null;
  }

  private createEmptyCardsByColumn(): Record<string, DynamicBoardCard[]> {
    return this.columns.reduce<Record<string, DynamicBoardCard[]>>((acc, column) => {
      acc[column.id] = [];
      return acc;
    }, {});
  }

  private resetCardsForCurrentColumns(): void {
    this.cardsByColumn = this.createEmptyCardsByColumn();
    this.columnPagination = {};
    this.locallyRemovedCardIds.clear();
    this.clearSelection();
  }

  private removeCardFromLocalState(cardId: string, columnId?: string): void {
    if (this.locallyRemovedCardIds.has(cardId)) return;
    this.locallyRemovedCardIds.add(cardId);

    const removedColumnId = this.removeCardFromVisibleState(cardId);

    if (removedColumnId) {
      this.updateColumnCounts(removedColumnId, -1);
    } else if (columnId) {
      this.updateColumnCounts(columnId, -1);
    }

    this.removeCardViewers(cardId);
  }

  private removeCardFromVisibleState(cardId: string): string | null {
    let removedColumnId: string | null = null;
    const next = { ...this.cardsByColumn };

    for (const [currentColumnId, cards] of Object.entries(this.cardsByColumn)) {
      const index = cards.findIndex(card => card.id === cardId);
      if (index === -1) continue;

      next[currentColumnId] = cards.filter(card => card.id !== cardId);
      removedColumnId = currentColumnId;
      break;
    }

    if (removedColumnId) {
      this.cardsByColumn = next;
      this.refillColumnIfNeeded(removedColumnId);
    }

    return removedColumnId;
  }

  private removeCardViewers(cardId: string): void {
    if (this.selectedCardIds.has(cardId)) {
      const nextSelected = new Set(this.selectedCardIds);
      nextSelected.delete(cardId);
      this.selectedCardIds = nextSelected;
    }

    if (!this.cardViewersById[cardId]) return;

    const viewers = { ...this.cardViewersById };
    delete viewers[cardId];
    this.cardViewersById = viewers;
  }

  onCardClick(card: BoardCard): void {
    this.openCardDetail(card.id, card);
  }

  private openCardFromUrl(): void {
    const [path] = this.location.path().split('?');
    const segments = path.split('/');
    const cardIndex = segments.indexOf('detail');
    const cardId = cardIndex !== -1 ? segments[cardIndex + 1] : undefined;

    if (cardId) {
      this.openCardDetail(cardId);
    }
  }

  private openCardDetail(cardId: string, card?: BoardCard): void {
    this.addCurrentViewer(cardId);
    this.setCardIdInUrl(cardId);

    this.stackedSidebarService
      .openSidebar(
        {
          type: SidebarType.CardV3,
          width: '90vw',
        },
        {
          card_id: cardId,
          type: this.type,
        },
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(result => {
        this.removeCurrentViewer(cardId);
        this.setCardIdInUrl();
        const savedCard = (result as { card?: BoardCard } | undefined)?.card;
        if (card && savedCard) {
          this.applyUpdatedCard({ ...card, ...savedCard });
        }
      });
  }

  private setCardIdInUrl(cardId?: string): void {
    const [path, query] = this.location.path().split('?');
    const segments = path.split('/');
    const cardIndex = segments.indexOf('detail');

    if (cardIndex !== -1 && segments[cardIndex + 1] === cardId) return;
    if (cardIndex === -1 && !cardId) return;

    const basePath = cardIndex !== -1 ? segments.slice(0, cardIndex) : segments;
    const newPath = cardId ? `${basePath.join('/')}/detail/${cardId}` : basePath.join('/');

    this.location.go(query ? `${newPath}?${query}` : newPath);
  }

  private applyCardPresence(cardId: string, viewers: CardViewer[]): void {
    this.cardViewersById = {
      ...this.cardViewersById,
      [cardId]: viewers,
    };
    let changed = false;
    const next = { ...this.cardsByColumn };

    for (const [columnId, cards] of Object.entries(this.cardsByColumn)) {
      const index = cards.findIndex(card => card.id === cardId);
      if (index === -1) continue;

      const nextCards = [...cards];
      nextCards[index] = {
        ...nextCards[index],
        viewers,
      };
      next[columnId] = nextCards;
      changed = true;
    }

    if (changed) {
      this.cardsByColumn = next;
    }
  }

  private addCurrentFunnelViewer(): void {
    const viewer = this.getCurrentViewer();
    if (!viewer) return;

    this.funnelViewers = [viewer, ...this.funnelViewers.filter(item => item.id !== viewer.id)];
  }

  private addCurrentViewer(cardId: string): void {
    const viewer = this.getCurrentViewer();
    if (!viewer) return;

    const current = this.cardViewersById[cardId] ?? [];
    this.applyCardPresence(cardId, [viewer, ...current.filter(item => item.id !== viewer.id)]);
  }

  private removeCurrentViewer(cardId: string): void {
    const viewer = this.getCurrentViewer();
    if (!viewer) return;

    const current = this.cardViewersById[cardId] ?? [];
    this.applyCardPresence(
      cardId,
      current.filter(item => item.id !== viewer.id),
    );
  }

  private getCurrentViewer(): CardViewer | null {
    const user = this.baseService.currentUserInfo || this.baseService.user;
    if (!user?.id) return null;

    return {
      id: String(user.id),
      first_name: user.first_name ?? '',
      last_name: user.last_name ?? '',
      avatar_url: user.avatar_url ?? null,
      email: user.email ?? '',
    };
  }
}
